<template>
    <div class="mb-5">
        <div v-if="testData">
            <!-- Overall test info -->
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-9">
                            <h2 class="card-title"> 
                                {{ testData.vehicle.manufacturer }} {{ testData.vehicle.model }} ({{ testData.vehicle.registration }})
                            </h2>
                        </div>
                        <div class="col-3">
                            <h2 class="card-title float-right">
                                <span class="text-muted" style="opacity: 0.6">RESULT: </span>
                                <span class="text-success" v-if="testData.test_passed">PASS</span>
                                <span class="text-danger" v-if="!testData.test_passed">FAIL</span>
                            </h2>
                        </div>
                    </div>
                    
                    <span class="badge badge-lg badge-secondary mt-1 mr-2">
                        <span class="fa fa-info-circle mr-1"></span>
                        TEST TYPE: {{ testData.test_type.toUpperCase() }}
                    </span>
                    <span class="badge badge-lg badge-secondary mt-1 mr-2" v-if="testData.time_start">
                        <span class="fa fa-clock-o mr-1"></span>
                        START: {{ $moment(testData.time_start).format('DD/MM/YYYY HH:mm') }}
                    </span>
                    <span class="badge badge-lg badge-secondary mt-1 mr-2" v-if="testData.time_end">
                        <span class="fa fa-clock-o mr-1"></span>
                        END: {{ $moment(testData.time_end).format('DD/MM/YYYY HH:mm') }}
                    </span>
                    <span class="badge badge-lg badge-secondary mt-1 mr-2" v-if="testData.inspector_name">
                        <span class="fa fa-user mr-1"></span>
                        TESTER: {{ testData.inspector_name }}
                    </span>
                </div>
            </div>
            <!-- /Overall test info -->

            <!-- Vehicle info -->
            <div class="card mt-4">
                <div class="card-body">
                    <h5 class="card-title"><span class="fa fa-car mr-2"></span> Vehicle</h5>
                    <div class="row mt-4">
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Registration</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-car text-success"></span>
                                {{ testData.vehicle.registration }}
                            </div>
                        </div>
                        
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Make</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-car text-success"></span>
                                {{ testData.vehicle.manufacturer }}
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Model</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-car text-success"></span>
                                {{ testData.vehicle.model }}
                            </div>
                        </div>
                        
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Year</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-calendar-blank text-success"></span>
                                {{ testData.vehicle.year }}
                            </div>
                        </div>
                    </div>

                    <div class="row mb-2">
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label"> Fuel Type</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-gas-station text-success"></span>
                                {{ testData.vehicle.fuel_type }}
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Engine Size</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-engine-outline text-success"></span>
                                {{ formatEngineSize() }}
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Mileage</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-speedometer text-success"></span>
                                {{ formatOdometerReading() }}
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Transmission</label>
                            <div class="data-item-value">
                                <span class="mdi mdi-car-shift-pattern text-success"></span>
                                {{ testData.vehicle.transmission ? testData.vehicle.transmission : 'NOT KNOWN' }}
                            </div>
                        </div>
                    </div>             
                </div>
            </div>
            <!-- /Vehicle info -->

            <!-- Vehicle images -->
            <div class="card mt-3">
                <div class="card-body">
                    <h5 class="card-title"><span class="fa fa-camera mr-2"></span> Vehicle Images</h5>

                    <p v-if="!testData.media || testData.media.length == 0">
                        No vehicle images were uploaded for this test
                    </p>

                    <div class="row row-5-pad mt-4">
                        <div class="col col-xs-6 col-sm-3 col-md-2 mb-2"                            
                            v-for="(media, idx) in testData.media.filter(m => !m.flagged_item_tags || m.flagged_item_tags.length == 0)" :key="idx">
                            <div class="picture-slide" @click="expandImage(testData.vehicle.registration, media.access_url)">
                                <b-img-lazy :src="media.access_url" blank-src="null" fluid></b-img-lazy>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- /Vehicle images -->            

            <!-- Test parameters -->
            <div class="card mt-3" v-if="testData.parameters && testData.parameters.length > 0">
                <div class="card-body">
                    <h5 class="card-title"><span class="fa fa-info-circle mr-2"></span> Test Parameters &amp; Limits</h5>
                    <div class="row mt-4">
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item" v-for="(item, idx) in testData.parameters.filter(p => p.field_value !== null)" :key="idx">
                            <label class="data-item-label text-muted">{{ getFormattedFieldName(item) }}</label>
                            <div class="data-item-value">{{ getFormattedFieldValue(item) }} {{ item.field_value_unit }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- /Test parameters -->

            <!-- Test Results -->
            <div class="card mt-3" v-for="stage in testStages" :key="stage.name">
                <div class="card-body">
                    <h5 class="card-title"><span class="fa fa-info-circle mr-2"></span> {{ stage.name }}</h5>                    
                    <div class="row mt-4">
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item" v-for="(item, idx) in stage.data.filter(d => d.field_value !== null)" :key="idx">
                            <label class="data-item-label text-muted">{{ getFormattedFieldName(item) }}</label>
                            <div class="data-item-value">{{ getFormattedFieldValue(item) }} {{ item.field_value_unit }}</div>                            
                        </div>
                    </div>
                </div>
            </div>
            <!-- /Test Results -->

            <!-- RFRs -->
            <div class="card mt-3">
                <div class="card-body">
                    <h5 class="card-title"><span class="fa fa-exclamation-circle mr-2"></span> Avisory &amp; Failure Items</h5>

                    <p class="mb-4">
                        {{ testData.flagged_items.filter(f => f.severity == 'Advisory').length }} Advisories / 
                        {{ testData.flagged_items.filter(f => f.severity == 'Fail').length }} Failures recorded
                    </p>
                    
                    <div v-for="(flaggedItem, idx) in testData.flagged_items" :key="idx">
                        <h6>
                            <span class="fa fa-warning text-warning mr-2" v-if="flaggedItem.severity == 'Advisory'"></span>
                            <span class="fa fa-ban text-danger mr-2" v-if="flaggedItem.severity == 'Fail'"></span>
                            <span :class="flaggedItem.severity == 'Fail' ? 'text-danger' : 'text-warning'"><strong>{{ flaggedItem.severity.toUpperCase() }}:</strong></span>
                            {{ flaggedItem.name }}
                        </h6>
                        <p>
                            {{ flaggedItem.description }}
                            <span class="text-muted" v-if="!flaggedItem.description">(No description supplied)</span>
                        </p>

                        <div class="row row-5-pad">
                            <div class="col col-xs-6 col-sm-3 col-md-2 mb-2"
                                 v-for="(media, idx) in testData.media.filter(m => m.flagged_item_tags && m.flagged_item_tags.includes(flaggedItem.item_identifier))" :key="idx">
                                <div class="picture-slide" @click="expandImage(flaggedItem.name, media.access_url)">
                                    <b-img-lazy :src="media.access_url" blank-src="null" fluid></b-img-lazy>
                                </div> 
                            </div>
                        </div>

                        <hr class="mb-4 mt-2" v-if="testData.flagged_items.length > 0 && idx < testData.flagged_items.length - 1">
                    </div>
                </div>
            </div>
            <!-- /RFRs -->

            <!-- Equipment Info -->
            <div class="card mt-3">
                <div class="card-body">
                    <h5 class="card-title"><span class="fa fa-desktop mr-2"></span> Equipment Used</h5>

                    <div class="row mt-4">
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Type</label>
                            <div class="data-item-value">
                                {{ testData.equipment_used.equipment_type }}
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Manufacturer</label>
                            <div class="data-item-value">
                                {{ testData.equipment_used.manufacturer }}
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Model</label>
                            <div class="data-item-value">                                
                                {{ testData.equipment_used.model }}
                                <span class="text-very-muted" v-if="!testData.equipment_used.model">N/S</span>
                            </div>
                        </div>

                        <div class="col-xs-6 col-sm-4 col-md-3 data-item">
                            <label class="data-item-label">Serial Number</label>
                            <div class="data-item-value">
                                {{ testData.equipment_used.serial_number }}
                                <span class="text-very-muted" v-if="!testData.equipment_used.serial_number">N/S</span>
                            </div>
                        </div>
                        
                        <div class="col-xs-6 col-sm-4 col-md-3 data-item" v-for="(item, idx) in testData.equipment_used.metadata" :key="idx">
                            <label class="data-item-label">{{ getFormattedFieldName(item) }}</label>
                            <div class="data-item-value">{{ getFormattedFieldValue(item) }}</div>
                        </div>
                    </div>                   

                </div>
            </div>
            <!-- /Equipment Info -->            

            <!-- Image preview modal -->
            <b-modal id="modalImageExpand" ok-only hide-header-close scrollable size="xl" :title="selectedImageTitle">
                <b-img :src="selectedImageUrl" fluid />
            </b-modal>

            <!-- <pre class="mt-2">{{ testData }}</pre> -->
        </div>

        <h4 v-if="viewState == 'Loading'">Loading...</h4>
        <h4 v-if="viewState == 'Error'">Error </h4>
    </div>
</template>

<script>
    import { TestService } from '../../services/testService'
    import _ from 'lodash'

    export default {
        props: {
            testId: String
        },
        data() {
            return {
                viewState: 'Loading',
                testData: null,
                selectedImageTitle: '',
                selectedImageUrl: '',
                testStages: []
            }
        },
        async mounted() {
            try {
                let testResult = await TestService.getVehicleTestById(this.testId)

                // Build an array of test stages, starting with any data that is un-named
                let uncategorisedTestData = testResult.data.filter(d => !d.test_stage || d.test_stage.length == 0)
                if (uncategorisedTestData.length > 0) {
                    this.testStages.push({ name: 'Test Results', data: uncategorisedTestData })
                }

                // Add test data with named stages
                if (testResult.test_stages && testResult.test_stages.length > 0) {
                    testResult.test_stages.forEach(stage => {
                        let stageData = testResult.data.filter(d => d.test_stage == stage.name)
                        this.testStages.push({ name: stage.label || _.startCase(stage.name), data: stageData })
                    })
                }

                this.testData = testResult
                this.viewState = 'Idle'
            } catch (error) {
                this.viewState = 'Error'
                console.error(error)
            }
        },
        methods: {
            expandImage(title, imgUrl) {
                this.selectedImageTitle = `Supporting Image: ${title}`
                this.selectedImageUrl = imgUrl
                this.$bvModal.show('modalImageExpand')
            },
            getFormattedFieldValue(field) {
                if (!field.field_data_type) 
                    return field.field_value

                let val = field.field_value
                let strFormatted = val               

                switch (field.field_data_type) {
                    case 'Date':
                    case 'DateTime':
                        let mDateTime = this.$moment(val)
                        strFormatted = mDateTime.isValid() ? mDateTime.format('DD/MM/YYYY HH:mm') : val
                        break;
                    
                    case 'Double':
                        let dVal = parseFloat(val)
                        strFormatted = dVal ? dVal.toFixed(2) : val
                        break;

                    case 'Boolean':
                        strFormatted = val ? 'YES' : 'NO'
                        break;
                }
                return strFormatted
            },
            getFormattedFieldName(field) {
                return field.field_label || _.startCase(field.field_name || field.field_identifier)
            },
            formatOdometerReading() {
                let vehicle = this.testData.vehicle
                return vehicle.odometer_reading && vehicle.odometer_reading > 0 ? 
                    `${vehicle.odometer_reading} ${vehicle.odometer_reading_unit || 'miles'}` :
                    'NOT KNOWN'
            },
            formatEngineSize() {
                let vehicle = this.testData.vehicle
                return vehicle.engine_cc && vehicle.engine_cc > 0 ?
                    `${vehicle.engine_cc} CC` : 'NOT KNOWN'
            }
        }
    }
</script>

<style lang="scss">
    .picture-slide {
        background: lightgray;
        padding: 4px;
        cursor: pointer;
    }

    .text-very-muted {
        color: lightgray;    
    }

    .row-5-pad {
        margin-right: -5px !important;
        margin-left:  -5px !important;

        .col {
            padding-right: 5px;
            padding-left: 5px;
        }
    }
</style>